import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@heyroot/ui/primitives/Button';
import { Input } from '@heyroot/ui/primitives/Input';
import { createAdminRole, queryKeys, AdminApiError } from '../lib/api';
import type { PermName } from '../../rbac/permissions';
import { PermissionBitPicker } from '../components/roles/permission-bit-picker';

const ROLE_ID_PATTERN = /^[a-z][a-z0-9_]*$/;

const ERROR_COPY: Record<string, string> = {
  ROLE_EXISTS: 'A role with that id already exists. Pick a different id.',
  UNKNOWN_PERMISSION: 'One of the selected permissions no longer exists. Reload the page.',
};

export function RoleNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [roleId, setRoleId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<string[]>([]);
  const [formError, setFormError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      // Key omitted rather than set to `undefined` -- exactOptionalPropertyTypes
      // rejects an explicit undefined for an optional property.
      createAdminRole({
        roleId,
        name,
        ...(description ? { description } : {}),
        permissions: permissions as PermName[],
      }),
    onSuccess: async () => {
      setFormError(null);
      await qc.invalidateQueries({ queryKey: queryKeys.adminRoles });
      void navigate({ to: '/admin-roles/$roleId', params: { roleId } });
    },
    onError: (err) => {
      setFormError(
        err instanceof AdminApiError
          ? (ERROR_COPY[err.code] ?? err.message)
          : 'Could not create this role.',
      );
    },
  });

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/admin-roles"
        className="inline-flex items-center gap-1 text-body-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={14} /> Roles
      </Link>

      <div>
        <h1 className="text-heading-2 text-primary">New role</h1>
      </div>

      {formError && (
        <div
          role="alert"
          className="rounded-control border border-danger bg-danger-pale text-danger-text text-body-sm p-3"
        >
          {formError}
        </div>
      )}

      <div className="flex max-w-xl flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Role id</span>
          <Input
            required
            value={roleId}
            onChange={(e) => setRoleId(e.target.value)}
            invalid={roleId.length > 0 && !ROLE_ID_PATTERN.test(roleId)}
          />
          <span className="text-body-sm text-muted">
            Lowercase letters, digits and underscores only. Cannot be changed later.
          </span>
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Name</span>
          <Input required value={name} onChange={(e) => setName(e.target.value)} />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-body-sm-emphasis text-secondary">Description</span>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
      </div>

      <section>
        <h2 className="text-eyebrow uppercase text-muted mb-3">Permissions</h2>
        <PermissionBitPicker selected={permissions} onChange={setPermissions} />
      </section>

      <div className="flex gap-2">
        <Button
          onClick={() => create.mutate()}
          loading={create.isPending}
          disabled={!roleId || !name || !ROLE_ID_PATTERN.test(roleId)}
        >
          Create
        </Button>
        <Button variant="secondary" onClick={() => void navigate({ to: '/admin-roles' })}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
